import type { Signal } from '@preact/signals-react';
import { useSignals } from '@preact/signals-react/runtime';
import type { MouseEvent } from 'react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OdinDataRetrieval } from '../../common/data-retrieval/data-retrieval-interfaces';
import { useEvent } from '../../common/hooks/use-event';
import useTestTag from '../../common/hooks/use-test-tag';
import { OverlayScrollDetectionType } from '../common/overlay-panel/overlay-panel.props';
import { mergeStyling } from '../common-functions';
import type {
  ContinuousScrollerFilter,
  ContinuousScrollerSort,
  ContinuousScrollerSortDirection,
} from '../continuous-scroller/continuous-scroller-types';
import OdinIcon, { OdinIconSize } from '../odin-icon/odin-icon.component';
import type { DataGridColumnSettings } from './data-grid-types';
import DataGridDateFilter from './filters/data-grid-date-filter.component';
import DataGridDropDownFilter from './filters/data-grid-drop-down-filter.component';
import DataGridNumericFilter from './filters/data-grid-numeric-filter.component';
import DataGridSearchFilter from './filters/data-grid-search-filter.component';
import { ABSOLUTE_MIN_COLUMN_WIDTH_PX, getColumnDefaultWidth } from './functions/data-grid-functions';

interface DataGridColumnProps {
  columns: Signal<Array<DataGridColumnSettings>>;
  column: DataGridColumnSettings;
  search?: OdinDataRetrieval;
  filterSignal: Signal<Array<ContinuousScrollerFilter>>;
  sortSignal: Signal<Array<ContinuousScrollerSort>>;
  scrollDetection?: OverlayScrollDetectionType;
  filterResetSignal: Signal<number>;
  onColumnResized: (columnId: string, newWidth: number) => void;
  onColumnResizing: (columnId: string, liveWidth: number) => void;
  disableResize?: boolean;
}

export default function DataGridColumn({
  columns,
  column,
  search,
  filterSignal,
  sortSignal,
  scrollDetection = OverlayScrollDetectionType.Parent,
  filterResetSignal,
  onColumnResized,
  onColumnResizing,
  disableResize,
}: DataGridColumnProps) {
  useSignals();
  const [isResizing, setIsResizing] = useState(false);
  const startingColumnWidth = useRef(column.width ?? getColumnDefaultWidth(column));
  const dragStartPosition = useRef(0);
  const latestPageX = useRef(0);
  const rafId = useRef<number | null>(null);
  const columnRef = useRef<HTMLDivElement>(null);

  const columnSort = column.disableSort ? undefined : sortSignal.value.find((field) => field.scrollerId === column.id);

  const columnId = useId();
  const tagCreator = useTestTag(column.title || '');

  const computeClampedWidth = (pageX: number) =>
    Math.max(ABSOLUTE_MIN_COLUMN_WIDTH_PX, startingColumnWidth.current + (pageX - dragStartPosition.current));

  const stableMouseMove = useEvent((event: globalThis.MouseEvent) => {
    latestPageX.current = event.pageX;
    if (rafId.current === null) {
      rafId.current = requestAnimationFrame(() => {
        rafId.current = null;
        const liveWidth = computeClampedWidth(latestPageX.current);
        onColumnResizing(String(column.id), liveWidth);
      });
    }
  });

  const stableMouseUp = useEvent((event: globalThis.MouseEvent) => {
    if (rafId.current !== null) {
      cancelAnimationFrame(rafId.current);
      rafId.current = null;
    }

    const clampedWidth = computeClampedWidth(event.pageX);
    startingColumnWidth.current = clampedWidth;

    onColumnResized(String(column.id), clampedWidth);

    document.removeEventListener('mousemove', stableMouseMove);
    document.removeEventListener('mouseup', stableMouseUp);

    document.body.style.userSelect = '';
    document.body.style.pointerEvents = '';
    setIsResizing(false);
  });

  // Safety net: if the column unmounts mid-drag (e.g. visibility toggled, columns refetched),
  // the document-level listeners and global body styles must still be cleared.
  useEffect(() => {
    if (!isResizing) return;
    return () => {
      document.removeEventListener('mousemove', stableMouseMove);
      document.removeEventListener('mouseup', stableMouseUp);
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
      document.body.style.userSelect = '';
      document.body.style.pointerEvents = '';
    };
  }, [isResizing, stableMouseMove, stableMouseUp]);

  const onDragStart = (event: MouseEvent) => {
    const effectiveWidth =
      columnRef.current?.getBoundingClientRect().width ?? column.width ?? getColumnDefaultWidth(column);
    startingColumnWidth.current = effectiveWidth;
    dragStartPosition.current = event.pageX;
    latestPageX.current = event.pageX;

    // Cap the resize indicator's height to the visible scroll viewport so its
    // 100vh fallback can never trigger an overflow scrollbar on short grids.
    const scroller = columnRef.current?.closest<HTMLElement>('[role="grid"]');
    if (scroller) {
      scroller.style.setProperty('--grid-scroll-h', `${scroller.clientHeight}px`);
    }

    document.body.style.userSelect = 'none';
    document.body.style.pointerEvents = 'none';
    setIsResizing(true);

    document.addEventListener('mousemove', stableMouseMove);
    document.addEventListener('mouseup', stableMouseUp);
  };

  const handleSort = (direction: ContinuousScrollerSortDirection) => {
    const existingIndex = sortSignal.value.findIndex((s) => s.scrollerId === column.id);
    if (existingIndex > -1) {
      sortSignal.value = sortSignal.value.map((s, i) =>
        i === existingIndex ? { ...s, sortDirection: direction, customDataProperties: column.customDataProperties } : s
      );
    } else {
      sortSignal.value = [
        ...sortSignal.value,
        {
          scrollerId: column.id,
          field: column.field,
          sortPath: column.sortPath,
          customDataProperties: column.customDataProperties,
          sortDirection: direction,
          order: sortSignal.value.length + 1,
        },
      ];
    }
  };

  const handleRemoveSort = () => {
    sortSignal.value = sortSignal.value
      .filter((s) => s.scrollerId !== column.id)
      .map((s, i) => ({ ...s, order: i + 1 }));
  };

  const handleSortClick = () => {
    if (!columnSort) {
      handleSort('ascending');
    } else if (columnSort.sortDirection === 'ascending') {
      handleSort('descending');
    } else {
      handleRemoveSort();
    }
  };

  const filterComponent = useMemo(() => {
    switch (column.searchType) {
      case 'TextSearch':
        return (
          <DataGridSearchFilter
            column={column}
            filterSignal={filterSignal}
            scrollDetection={scrollDetection}
            id={columnId}
            filterResetSignal={filterResetSignal}
          />
        );
      case 'MultiSelect':
        return (
          <DataGridDropDownFilter
            columns={columns}
            search={search}
            column={column}
            filterSignal={filterSignal}
            scrollDetection={scrollDetection}
            filterResetSignal={filterResetSignal}
          />
        );
      case 'DateRange':
        return (
          <DataGridDateFilter
            column={column}
            filterSignal={filterSignal}
            scrollDetection={scrollDetection}
            filterResetSignal={filterResetSignal}
          />
        );
      case 'Integer':
        return (
          <DataGridNumericFilter
            column={column}
            filterSignal={filterSignal}
            scrollDetection={scrollDetection}
            filterResetSignal={filterResetSignal}
          />
        );
      case 'Decimal':
        return (
          <DataGridNumericFilter
            type="decimal"
            column={column}
            filterSignal={filterSignal}
            scrollDetection={scrollDetection}
            filterResetSignal={filterResetSignal}
          />
        );
      default:
        return null;
    }
  }, [column, columnId, columns, filterSignal, filterResetSignal, scrollDetection, search]);

  return (
    <div ref={columnRef} className="group/header flex h-full bg-gray-5 hover:bg-gray-450">
      <div
        className="group flex min-w-0 grow flex-col gap-1.5 p-2"
        ref={(el) => {
          tagCreator('column')(el);
        }}
      >
        <ColumnSortHeader
          column={column}
          columnSort={columnSort}
          sortCount={sortSignal.value.length}
          onSortClick={handleSortClick}
        />
        {filterComponent && <div className="grow font-normal text-sm">{filterComponent}</div>}
      </div>
      {!disableResize && <ResizeHandle columnTitle={column.title} isResizing={isResizing} onMouseDown={onDragStart} />}
    </div>
  );
}

const HEADER_LABEL_CLASSES = mergeStyling(
  'flex shrink items-center gap-1 text-left font-medium text-gray-1 text-sm leading-5'
);

function ColumnSortHeader({
  column,
  columnSort,
  sortCount,
  onSortClick,
}: {
  column: DataGridColumnSettings;
  columnSort: ContinuousScrollerSort | undefined;
  sortCount: number;
  onSortClick: () => void;
}) {
  if (column.disableSort) {
    return (
      <div className={HEADER_LABEL_CLASSES}>
        <span className="truncate">{column.title || ''}</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      className={mergeStyling(HEADER_LABEL_CLASSES, 'w-full select-none justify-between')}
      onClick={onSortClick}
    >
      <span className="truncate">{column.title || ''}</span>
      <SortIcon columnSort={columnSort} sortCount={sortCount} />
    </button>
  );
}

function SortIcon({ columnSort, sortCount }: { columnSort: ContinuousScrollerSort | undefined; sortCount: number }) {
  if (!columnSort) {
    return (
      <span className="shrink-0 opacity-0 group-hover/header:opacity-100">
        <OdinIcon icon="ArrowUpDown" size={OdinIconSize.ExtraSmall} />
      </span>
    );
  }

  return (
    <span className="inline-flex shrink-0 items-center gap-0.5">
      <OdinIcon
        icon={columnSort.sortDirection === 'ascending' ? 'ArrowUp' : 'ArrowDown'}
        size={OdinIconSize.ExtraSmall}
      />
      {sortCount > 1 && <span className="text-xxs">{columnSort.order}</span>}
    </span>
  );
}

function ResizeHandle({
  columnTitle,
  isResizing,
  onMouseDown,
}: {
  columnTitle: string | undefined;
  isResizing: boolean;
  onMouseDown: (event: MouseEvent) => void;
}) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      tabIndex={0}
      aria-label={t('resize-column', { column: columnTitle || 'column' })}
      className="absolute top-0 right-0 z-10 flex h-full translate-x-1/2 cursor-col-resize items-center px-1.5 opacity-0 transition-opacity duration-150 group-hover/header:opacity-100 data-[resizing]:opacity-100"
      data-resizing={isResizing ? '' : undefined}
      onMouseDown={onMouseDown}
    >
      <span
        className={mergeStyling(
          'w-0.5 rounded-full bg-gray-4',
          isResizing ? 'absolute top-0 h-(--grid-scroll-h,100vh) bg-primary-2' : 'h-2/3'
        )}
      />
    </button>
  );
}
